import {
  ApolloClient,
  ApolloLink,
  createHttpLink,
  from,
  InMemoryCache,
  Observable,
} from '@apollo/client/core'
import { onError } from '@apollo/client/link/error'
import { RetryLink } from '@apollo/client/link/retry'
import type {
  CopyrightTransaction,
  Wallet,
} from '@/shared/graphql/legacy/generated'
import { checkAccessToken, getTokens } from '@dacr/auth'
import { useWebviewStore } from '@/shared/stores/webview'
import { analytics } from '@/shared/services/analytics'

const httpLink = createHttpLink({
  uri: import.meta.env.VITE_GRAPHQL_API,
})

const routerHttpLink = createHttpLink({
  uri: import.meta.env.VITE_GRAPHQL_ROUTER_API,
})

const authLink = new ApolloLink((operation, forward) => {
  return new Observable((observer) => {
    const run = async () => {
      const webviewStore = useWebviewStore()

      if (!webviewStore.isActive) {
        await checkAccessToken()
      }

      const { accessToken } = getTokens()

      operation.setContext({
        headers: {
          authorization: accessToken ? `Bearer ${accessToken}` : null,
          'dacr-platform': 'web',
        },
      })

      forward(operation).subscribe(observer)
    }

    run().catch((err) => observer.error(err))
  })
})

const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  if (graphQLErrors) {
    analytics.captureGraphQLError(operation.operationName, graphQLErrors)
    graphQLErrors.forEach(({ extensions }) => {
      if (extensions?.code === 'UNAUTHENTICATED') {
        // Token refresh or redirect to login handled by authLink
      }
    })
  }
  if (networkError) {
    analytics.captureException(
      networkError instanceof Error
        ? networkError
        : new Error(String(networkError))
    )
  }
})

const retryLink = new RetryLink({
  delay: { initial: 300, max: 5000, jitter: true },
  attempts: { max: 3 },
})

export const routerApolloClient = new ApolloClient({
  name: 'web',
  link: from([errorLink, authLink, retryLink, routerHttpLink]),
  cache: new InMemoryCache({
    typePolicies: {
      ImageMatch: {
        keyFields: false,
      },
      AudioMatch: {
        keyFields: false,
      },
      TextMatch: {
        keyFields: false,
      },
      VideoMatch: {
        keyFields: false,
      },
      TranscriptMatch: {
        keyFields: false,
      },
    },
  }),
})

export const apolloClient = new ApolloClient({
  link: from([errorLink, authLink, retryLink, httpLink]),
  cache: new InMemoryCache({
    typePolicies: {
      Sub: {
        keyFields: () => 'Subscription',
      },
      FolderByPathResponse: {
        keyFields: ['id', 'sortBy'],
        fields: {
          items: {
            merge(existing = [], incoming = []) {
              return incoming || existing
            },
          },
        },
      },
      File: {
        fields: {
          transactions: {
            merge(existing = [], incoming = []) {
              return existing.length ? existing : incoming
            },
          },
          analysis: {
            merge(existing, incoming) {
              return Object.keys(existing || {}).length ? existing : incoming
            },
          },
        },
      },
      License: {
        fields: {
          originalCopyright: {
            merge(existing, incoming) {
              return existing || incoming
            },
          },
          owner: {
            merge(existing, incoming) {
              return existing || incoming
            },
          },
          timestamp: {
            merge(existing, incoming) {
              return existing || incoming
            },
          },
        },
      },
      CopyrightTransaction: {
        keyFields: (data) => {
          return `CopyrightTransaction:${
            (data as CopyrightTransaction).transaction.hash
          }`
        },
      },
      BasicUser: {
        keyFields: false,
      },
      Wallet: {
        keyFields: (data) => {
          return `Wallet:${(data as Wallet).address}`
        },
      },
      Match: {
        keyFields: false,
      },
      BasicFile: {
        keyFields: false,
      },
      Registration: {
        keyFields: false,
      },
    },
  }),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
    },
  },
})
